import { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover } from 'react-tiny-popover';
import { ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SellerInventoryForBuyer } from '~/types/inventory';
import {
  useVendorGroupFacilities,
  type VendorFacilitiesResult,
} from './useVendorGroupFacilities';
import {
  DRILL_DOWN_PAGE_SIZE,
  FACILITY_PICKER_PAGE_SIZE,
} from './vendorGroupFacilitiesConstants';
import type { SellerProductInventoriesForBuyerFilters } from '~/api/inventory';
import { facilityAddressLabel } from '~/utils/facilityOptions';

interface FacilityPickerProps {
  organizationId: string;
  platformProductLengthId?: string;
  platformProductId?: string;
  facilityCount: number;
  baseFilter: Pick<
    SellerProductInventoriesForBuyerFilters,
    'destinationLocationId' | 'deliveryMode' | 'includeInventoryLengths'
  >;
  disabled?: boolean;
  disabledReason?: string;
  accessibleLabel: string;
  label?: string;
  // Fallback price for a facility with no listed price of its own — e.g. a buyer-negotiated
  // price set for the vendor group. Undefined means only individually priced facilities are
  // selectable.
  suggestedPrice?: number;
  bypassPricedGate?: boolean;
  onSelectFacility: (
    inventory: SellerInventoryForBuyer,
    suggestedPrice: number | undefined
  ) => void;
}

function facilityIsPriced(
  inventory: SellerInventoryForBuyer,
  suggestedPrice: number | undefined
): boolean {
  return inventory.unitPrice?.price != null || suggestedPrice != null;
}

function facilityMatchesTerm(
  inventory: SellerInventoryForBuyer,
  term: string
): boolean {
  const normalized = term.toLowerCase();
  return (
    (inventory.facility.code ?? '').toLowerCase().includes(normalized) ||
    facilityAddressLabel(inventory.facility).toLowerCase().includes(normalized)
  );
}

export function FacilityPicker({
  organizationId,
  platformProductLengthId,
  platformProductId,
  facilityCount,
  baseFilter,
  disabled,
  disabledReason,
  accessibleLabel,
  label,
  suggestedPrice,
  bypassPricedGate,
  onSelectFacility,
}: Readonly<FacilityPickerProps>) {
  const { t } = useTranslation('order-builder');
  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState('');

  const vendorGroupFacilitiesFilter = useMemo(
    () => ({
      platformProductLengthIds: platformProductLengthId
        ? [platformProductLengthId]
        : undefined,
      platformProductId: platformProductId ?? undefined,
      destinationLocationId: baseFilter.destinationLocationId ?? undefined,
      deliveryMode: baseFilter.deliveryMode ?? undefined,
      includeInventoryLengths: baseFilter.includeInventoryLengths ?? undefined,
    }),
    [
      platformProductLengthId,
      platformProductId,
      baseFilter.destinationLocationId,
      baseFilter.deliveryMode,
      baseFilter.includeInventoryLengths,
    ]
  );

  const expandedOrgIds = useMemo(
    () => (isOpen ? new Set([organizationId]) : new Set<string>()),
    [isOpen, organizationId]
  );

  const { facilitiesByOrgId, loadMoreFacilities } = useVendorGroupFacilities(
    [organizationId],
    expandedOrgIds,
    vendorGroupFacilitiesFilter,
    platformProductLengthId ? DRILL_DOWN_PAGE_SIZE : FACILITY_PICKER_PAGE_SIZE
  );

  const result: VendorFacilitiesResult | undefined =
    facilitiesByOrgId.get(organizationId);
  const isSelectable = useCallback(
    (inventory: SellerInventoryForBuyer) =>
      bypassPricedGate || facilityIsPriced(inventory, suggestedPrice),
    [bypassPricedGate, suggestedPrice]
  );
  const content = useMemo(() => {
    const rows = (result?.content ?? []).filter(
      (inventory): inventory is SellerInventoryForBuyer =>
        inventory != null &&
        inventory.facility != null &&
        Boolean(inventory.facility.id)
    );
    if (platformProductLengthId) return rows;
    const byFacilityId = new Map<string, SellerInventoryForBuyer>();
    rows.forEach((inventory) => {
      const existing = byFacilityId.get(inventory.facility.id);
      if (!existing || isSelectable(inventory)) {
        byFacilityId.set(inventory.facility.id, inventory);
      }
    });
    return [...byFacilityId.values()];
  }, [result, platformProductLengthId, isSelectable]);

  // Single-facility sellers auto-resolve as soon as that facility loads, closing the picker
  // without ever rendering it — the buyer never sees a popover with one entry to click. Skipped on
  // error so the retry UI below gets a chance to render instead of silently doing nothing.
  useEffect(() => {
    if (
      facilityCount === 1 &&
      isOpen &&
      content.length === 1 &&
      !result?.error &&
      isSelectable(content[0])
    ) {
      onSelectFacility(content[0], suggestedPrice);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsOpen(false);
    }
  }, [
    facilityCount,
    isOpen,
    content,
    result?.error,
    suggestedPrice,
    isSelectable,
    onSelectFacility,
  ]);

  function close() {
    setIsOpen(false);
    setTerm('');
  }

  const matches = useMemo(
    () => content.filter((inventory) => facilityMatchesTerm(inventory, term)),
    [content, term]
  );

  // A single-facility seller normally auto-resolves via the effect above and never reaches this
  // render; it falls through here only when there's nothing to auto-select — a load error, an
  // empty result, or an unpriced facility with no suggestedPrice — so the buyer needs the popover
  // open to see why the button did nothing.
  const singleFacilityNeedsPopover =
    facilityCount === 1 &&
    !result?.isLoading &&
    (result?.error != null ||
      content.length !== 1 ||
      !isSelectable(content[0]));

  return (
    <Popover
      isOpen={isOpen && (facilityCount > 1 || singleFacilityNeedsPopover)}
      positions={['bottom', 'top']}
      align="end"
      padding={4}
      containerStyle={{ zIndex: '1100' }}
      clickOutsideCapture
      onClickOutside={close}
      content={
        <div
          className="w-72 bg-neut-0 border border-neut-200 rounded-lg shadow-2xl"
          data-testid="facility-picker-popover"
        >
          <div className="p-2 border-b border-neut-100">
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={t(
                'order_builder.table.vendor_facilities_search_placeholder'
              )}
              className="w-full text-sm px-2 py-1.5 border border-neut-200 rounded"
            />
          </div>
          <ul className="max-h-64 overflow-auto">
            {matches.map((inventory) => {
              const isPriced = isSelectable(inventory);
              return (
                <li key={inventory.facility.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isPriced) return;
                      onSelectFacility(inventory, suggestedPrice);
                      close();
                    }}
                    disabled={!isPriced}
                    title={
                      isPriced
                        ? undefined
                        : t('order_builder.table.vendor_facility_no_price')
                    }
                    data-testid="facility-picker-option"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-prim-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <p className="font-medium text-neut-900">
                      {inventory.facility.code}
                    </p>
                    <p className="text-neut-500">
                      {facilityAddressLabel(inventory.facility)}
                    </p>
                  </button>
                </li>
              );
            })}
            {result?.error && (
              <li className="px-3 py-4 text-sm text-error-600">
                {t('order_builder.table.vendor_facilities_error')}{' '}
                <button
                  type="button"
                  onClick={() => result.refetch()}
                  className="underline hover:no-underline"
                >
                  {t('order_builder.table.vendor_facilities_retry')}
                </button>
              </li>
            )}
            {!result?.error && matches.length === 0 && !result?.isLoading && (
              <li className="px-3 py-4 text-sm text-neut-500">
                {result?.hasMore
                  ? t(
                      'order_builder.table.vendor_facilities_no_match_more_pages'
                    )
                  : t('order_builder.table.vendor_facilities_no_match')}
              </li>
            )}
            {!result?.error && result?.isLoading && (
              <li className="px-3 py-4 text-sm text-neut-500">
                {t('order_builder.table.loading_vendor_facilities')}
              </li>
            )}
            {result?.hasMore && (
              <li>
                <button
                  type="button"
                  onClick={() => loadMoreFacilities(organizationId)}
                  disabled={result.isLoadingMore}
                  className="w-full px-3 py-2 text-sm text-prim-700 hover:bg-prim-50 disabled:text-neut-400"
                >
                  {result.isLoadingMore
                    ? t('order_builder.table.loading_more_vendor_facilities')
                    : t('order_builder.table.vendor_facilities_load_more')}
                </button>
              </li>
            )}
          </ul>
        </div>
      }
    >
      <span
        title={disabled && disabledReason ? disabledReason : accessibleLabel}
        className="inline-flex items-center gap-2 shrink-0"
        data-testid="facility-picker-trigger-wrapper"
      >
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label={accessibleLabel}
          disabled={disabled}
          data-testid={`add-to-cart-${organizationId}-${platformProductLengthId ?? 'all-lengths'}`}
          className={`flex items-center gap-2 rounded-lg transition-colors bg-prim text-neut-light cursor-pointer hover:bg-prim-hover disabled:bg-neut-200 disabled:text-neut-400 disabled:cursor-not-allowed shrink-0 ${
            label ? 'py-3 px-4' : 'p-2'
          }`}
        >
          <ShoppingCart className="w-4 h-4 shrink-0" aria-hidden="true" />
          {label && (
            <span className="text-sm font-medium whitespace-nowrap">
              {label}
            </span>
          )}
        </button>
      </span>
    </Popover>
  );
}
